import type {
  Service,
  BlogPost,
  Testimonial,
  TeamMember,
  Resource,
  MediaItem,
  SiteConfig,
} from "@/types";

export const siteConfig: SiteConfig = {
  name: "Rosewood Consulting",
  tagline: "Trusted Tax & Advisory Solutions for Every Business",
  description:
    "Rosewood Consulting is a Nairobi-based tax and advisory firm helping businesses and individuals stay compliant, manage risk, and make sound financial decisions that drive measurable growth.",
  phone: "+254 703 173405",
  email: "info@rosewoodconsulting.co.ke",
  address: "Upperhill, Nairobi, Kenya",
  socialLinks: [
    { platform: "LinkedIn", url: "https://linkedin.com", icon: "linkedin" },
    { platform: "Twitter", url: "https://twitter.com", icon: "twitter" },
    { platform: "Facebook", url: "https://facebook.com", icon: "facebook" },
    { platform: "YouTube", url: "https://youtube.com", icon: "youtube" },
  ],
};

export const services: Service[] = [
  {
    id: "1",
    slug: "tax-advisory-planning",
    title: "Tax Advisory & Planning",
    shortDescription:
      "Kenya's tax landscape changes fast — new Finance Acts, new KRA directives, new risks. Our advisors stay ahead of every shift so you don't have to, helping you minimise exposure, unlock legitimate tax savings, and make decisions with confidence.",
    fullDescription:
      "Our Tax Advisory service gives you direct access to experts who understand both the letter and the practice of Kenyan tax law. We review your business structure, transactions, and contracts to identify hidden tax risks before KRA does — and pinpoint opportunities for relief, deductions, and efficient structuring. Whether you're planning an expansion, restructuring, or simply want certainty before a big decision, we provide clear, actionable guidance tailored to your situation.",
    image: "/images/services/tax-advisory.jpg",
    features: [
      "Business structure and transaction review",
      "Hidden tax risk identification",
      "Tax relief and deduction optimisation",
      "Expansion and restructuring advisory",
      "Pre-decision tax certainty reviews",
      "Contract and agreement tax analysis",
      "Tax savings opportunity identification",
    ],
    process: [
      {
        step: 1,
        title: "Initial Consultation",
        description:
          "Understand your business goals, structure, and current tax position.",
      },
      {
        step: 2,
        title: "Analysis & Research",
        description:
          "Deep review of transactions, contracts, and compliance exposure.",
      },
      {
        step: 3,
        title: "Strategy Development",
        description:
          "Develop a compliant, optimised tax strategy tailored to your situation.",
      },
      {
        step: 4,
        title: "Implementation Support",
        description:
          "Support implementation and provide ongoing guidance as circumstances evolve.",
      },
    ],
    faqs: [
      {
        question: "How can tax advisory save my business money?",
        answer:
          "Through legal tax planning — reviewing your structure, identifying legitimate deductions, and ensuring every transaction is structured efficiently before it happens rather than after.",
      },
      {
        question: "Do you provide written tax opinions?",
        answer:
          "Yes, we provide formal written opinions on complex tax matters that you can rely upon for decision-making.",
      },
    ],
    relatedServices: [
      "tax-returns-filing",
      "kra-audit-support",
      "advisory-growth-strategy",
    ],
    metaTitle: "Tax Advisory & Planning Kenya | Rosewood Consulting",
    metaDescription:
      "Strategic tax advisory and planning for Kenyan businesses. Identify risks, unlock savings, and make confident decisions with Rosewood Consulting.",
  },
  {
    id: "2",
    slug: "tax-returns-filing",
    title: "Tax Returns Preparation & Filing",
    shortDescription:
      "Accurate, on-time filing of all your returns — income tax, VAT, PAYE, withholding tax, and more. We handle the complexity on iTax so you avoid penalties and stay fully compliant year-round.",
    image: "/images/services/tax-returns.jpeg",
    fullDescription:
      "We prepare and file returns for individuals, sole proprietors, partnerships, and companies. Our process starts with a review of your records to ensure every figure is supported, every deduction claimed, and every deadline met. We also handle nil returns, amended returns, and back-filing for clients catching up on past periods, plus penalty waiver applications where applicable. With Rosewood, tax season stops being a crisis and becomes routine.",
    features: [
      "Income tax returns (individuals, sole proprietors, partnerships, companies)",
      "VAT returns preparation and filing",
      "PAYE returns processing",
      "Withholding tax returns",
      "Nil returns filing",
      "Amended returns submission",
      "Back-filing for prior periods",
      "Penalty waiver applications",
    ],
    process: [
      {
        step: 1,
        title: "Records Review",
        description:
          "We review your financial records to ensure every figure is supported and every deduction identified.",
      },
      {
        step: 2,
        title: "Tax Computation",
        description:
          "Accurate computation of tax liability with all applicable deductions claimed.",
      },
      {
        step: 3,
        title: "Client Approval",
        description:
          "We present the computations for your review and approval before filing.",
      },
      {
        step: 4,
        title: "Filing & Confirmation",
        description:
          "Returns filed on iTax with acknowledgement receipts provided.",
      },
    ],
    faqs: [
      {
        question: "When are tax returns due in Kenya?",
        answer:
          "Annual income tax returns are due by 30th June. VAT and PAYE returns are due on the 20th of the following month.",
      },
      {
        question: "What are the penalties for late filing?",
        answer:
          "Late filing attracts a penalty of 5% of the tax due or KES 20,000 (whichever is higher) plus monthly interest at 1%.",
      },
    ],
    relatedServices: [
      "tax-advisory-planning",
      "kra-audit-support",
      "bookkeeping-accounting",
    ],
    metaTitle: "Tax Returns Preparation & Filing Kenya | Rosewood Consulting",
    metaDescription:
      "Accurate and timely tax returns filing for individuals and businesses in Kenya. VAT, PAYE, income tax, withholding tax — handled on iTax by Rosewood Consulting.",
  },
  {
    id: "3",
    slug: "kra-audit-support",
    title: "KRA Audit Support & Dispute Resolution",
    shortDescription:
      "Facing a KRA audit, assessment, or demand letter? The stakes are high and the timelines are short. We respond, defend, and negotiate on your behalf — challenging arbitrary assessments and protecting your business.",
    image: "/images/services/kra-audit.webp",
    fullDescription:
      "A KRA audit doesn't have to end in a crippling assessment. Our team manages the entire process: responding to audit notices, compiling and presenting documentation, attending meetings with KRA officers, lodging objections, and pursuing resolution through Alternative Dispute Resolution (ADR) or the Tax Appeals Tribunal where necessary. We've helped clients reduce or overturn assessments by ensuring KRA follows due process and the law — not assumptions.",
    features: [
      "Audit notice response and representation",
      "Documentation compilation and presentation",
      "KRA meetings and officer engagement",
      "Tax assessment review and challenge",
      "Objection filing and management",
      "Alternative Dispute Resolution (ADR)",
      "Tax Appeals Tribunal representation",
      "Post-audit risk management",
    ],
    process: [
      {
        step: 1,
        title: "Audit Review",
        description:
          "Analyse the KRA notice, assess the scope, and identify the strongest defence strategy.",
      },
      {
        step: 2,
        title: "Defence Preparation",
        description:
          "Compile documentation, review records, and prepare a comprehensive response.",
      },
      {
        step: 3,
        title: "Representation",
        description:
          "Represent you in all KRA interactions, meetings, and correspondence.",
      },
      {
        step: 4,
        title: "Resolution",
        description:
          "Achieve a favourable settlement or escalate to ADR / Tax Appeals Tribunal where necessary.",
      },
    ],
    faqs: [
      {
        question: "What triggers a KRA audit?",
        answer:
          "Audits are triggered by risk profiling, sector-wide campaigns, tip-offs, discrepancies in returns, or random selection.",
      },
      {
        question: "Can I contest a KRA tax assessment?",
        answer:
          "Yes. You have 30 days from the date of assessment to file an objection with KRA. If unresolved, you can appeal to the Tax Appeals Tribunal.",
      },
    ],
    relatedServices: [
      "tax-advisory-planning",
      "tax-returns-filing",
      "tax-compliance-health-check",
    ],
    metaTitle: "KRA Audit Support & Dispute Resolution Kenya | Rosewood Consulting",
    metaDescription:
      "Expert KRA audit defence and tax dispute resolution in Kenya. Objections, ADR, and Tax Appeals Tribunal representation by Rosewood Consulting.",
  },
  {
    id: "4",
    slug: "tax-compliance-health-check",
    title: "Tax Compliance Health Check",
    shortDescription:
      "Not sure where you stand with KRA? Our compliance health check reviews your entire tax position — registrations, filings, payments, and records — and gives you a clear report of gaps, risks, and how to fix them.",
    image: "/images/services/compliance-health.webp",
    fullDescription:
      "Think of this as a full diagnostic before problems become penalties. We review your iTax ledger, registration obligations (VAT, PAYE, eTIMS, turnover tax), filing history, and supporting records, then deliver a prioritised action plan to bring you to full compliance. Ideal for businesses preparing for tenders, due diligence, financing, or simply wanting peace of mind before KRA comes knocking.",
    features: [
      "iTax ledger review",
      "Registration obligations assessment (VAT, PAYE, eTIMS, turnover tax)",
      "Filing history analysis",
      "Supporting records review",
      "Compliance gap identification",
      "Risk prioritisation report",
      "Remediation action plan",
      "Pre-tender and due diligence readiness",
    ],
    process: [
      {
        step: 1,
        title: "Data Collection",
        description:
          "Gather your iTax credentials, registration details, and financial records.",
      },
      {
        step: 2,
        title: "Diagnostic Review",
        description:
          "Systematically review all registration obligations, filings, payments, and records.",
      },
      {
        step: 3,
        title: "Gap Analysis",
        description:
          "Identify all compliance gaps, outstanding obligations, and risk areas.",
      },
      {
        step: 4,
        title: "Action Plan Delivery",
        description:
          "Deliver a clear, prioritised report with specific steps to achieve full compliance.",
      },
    ],
    faqs: [
      {
        question: "When should I get a compliance health check?",
        answer:
          "Before applying for tenders, during due diligence for financing or investment, before a business sale, or simply to get ahead of any KRA issues proactively.",
      },
      {
        question: "What if the health check reveals outstanding liabilities?",
        answer:
          "We provide a clear remediation plan and can manage the entire process of bringing you to full compliance, including penalty waiver applications where applicable.",
      },
    ],
    relatedServices: [
      "tax-advisory-planning",
      "tax-returns-filing",
      "kra-audit-support",
    ],
    metaTitle: "Tax Compliance Health Check Kenya | Rosewood Consulting",
    metaDescription:
      "Full tax compliance diagnostic for Kenyan businesses. Identify gaps, reduce risk, and get a clear action plan before KRA does — with Rosewood Consulting.",
  },
  {
    id: "5",
    slug: "bookkeeping-accounting",
    title: "Bookkeeping & Accounting Services",
    shortDescription:
      "Clean books are the foundation of compliance and growth. We handle your day-to-day bookkeeping, monthly management accounts, and reconciliations — so you always know your numbers and your records can stand up to scrutiny.",
    image: "/images/services/bookkeeping.webp",
    fullDescription:
      "We offer ongoing bookkeeping support tailored to your business size: recording transactions, reconciling bank and M-Pesa statements, managing receivables and payables, and producing monthly management reports you can actually use to make decisions. Structured bookkeeping isn't just good practice — it's what makes accurate tax filing, audit defence, and financing applications possible. We set up the system, keep it current, and keep you informed.",
    features: [
      "Day-to-day transaction recording",
      "Bank and M-Pesa statement reconciliation",
      "Receivables and payables management",
      "Monthly management accounts",
      "Financial report preparation",
      "Bookkeeping system setup",
      "Audit-ready record maintenance",
      "Financing application support",
    ],
    process: [
      {
        step: 1,
        title: "Onboarding",
        description:
          "Review existing records and set up or optimise your bookkeeping system.",
      },
      {
        step: 2,
        title: "Monthly Processing",
        description:
          "Record all transactions and reconcile bank and M-Pesa statements.",
      },
      {
        step: 3,
        title: "Reporting",
        description:
          "Prepare monthly management accounts and financial reports for decision-making.",
      },
      {
        step: 4,
        title: "Review & Advisory",
        description:
          "Walk you through the numbers and flag anything requiring attention.",
      },
    ],
    faqs: [
      {
        question: "What accounting software do you use?",
        answer:
          "We work with QuickBooks, Sage, Xero, and can adapt to your existing systems or set up a new solution tailored to your business.",
      },
      {
        question: "Why does clean bookkeeping matter for tax compliance?",
        answer:
          "Accurate, well-maintained records are the foundation of correct tax filing, a successful audit defence, and a credible financing or tender application.",
      },
    ],
    relatedServices: [
      "payroll-management",
      "tax-returns-filing",
      "tax-compliance-health-check",
    ],
    metaTitle: "Bookkeeping & Accounting Services Kenya | Rosewood Consulting",
    metaDescription:
      "Professional bookkeeping and accounting for Kenyan businesses. M-Pesa reconciliations, management accounts, and audit-ready records by Rosewood Consulting.",
  },
  {
    id: "6",
    slug: "payroll-management",
    title: "Payroll Management & PAYE Compliance",
    shortDescription:
      "From payslips to statutory deductions — PAYE, NSSF, SHIF, Housing Levy — we run your payroll accurately and on time, every month, while keeping you fully compliant with employment tax laws.",
    image: "/images/services/payroll.webp",
    fullDescription:
      "Payroll in Kenya means juggling multiple statutory obligations with shifting rates and deadlines. Our payroll service handles computation, payslip generation, statutory deductions and remittances, monthly returns, and year-end P9 forms for your employees. Whether you have two staff or two hundred, we ensure your team is paid correctly and your business is protected from payroll-related penalties.",
    features: [
      "Monthly payroll computation",
      "Payslip generation for all employees",
      "PAYE computation and remittance",
      "NSSF deductions and remittance",
      "SHIF (SHA) deductions and remittance",
      "Housing Levy computation and filing",
      "Monthly payroll returns filing",
      "Year-end P9 form preparation",
    ],
    process: [
      {
        step: 1,
        title: "Payroll Setup",
        description:
          "Capture employee details, salary structures, and statutory obligations.",
      },
      {
        step: 2,
        title: "Monthly Computation",
        description:
          "Process payroll, compute all statutory deductions, and generate payslips.",
      },
      {
        step: 3,
        title: "Returns & Remittances",
        description:
          "File PAYE, NSSF, SHIF, and Housing Levy returns and remit deductions on time.",
      },
      {
        step: 4,
        title: "Year-End Processing",
        description:
          "Prepare P9 forms and annual payroll reconciliation for each employee.",
      },
    ],
    faqs: [
      {
        question: "What statutory payroll deductions apply in Kenya?",
        answer:
          "Kenyan employers must deduct and remit PAYE, NSSF, SHIF (Social Health Insurance Fund), and the Housing Levy on behalf of employees.",
      },
      {
        question: "Can you manage payroll for large teams?",
        answer:
          "Yes. We handle payroll for businesses of all sizes — from two staff members to two hundred — with the same accuracy and reliability.",
      },
    ],
    relatedServices: [
      "bookkeeping-accounting",
      "tax-returns-filing",
      "business-registration",
    ],
    metaTitle: "Payroll Management & PAYE Compliance Kenya | Rosewood Consulting",
    metaDescription:
      "Accurate payroll management and PAYE compliance for Kenyan businesses. NSSF, SHIF, Housing Levy, and P9 forms handled by Rosewood Consulting.",
  },
  {
    id: "7",
    slug: "business-registration",
    title: "Business Registration & Start-Up Support",
    shortDescription:
      "Starting a business in Kenya? We handle company registration, KRA PIN registration, tax obligation setup, and everything you need to launch compliant from day one — for locals and foreign investors alike.",
    image: "/images/services/business-registration.jpg",
    fullDescription:
      "We take care of the full setup journey: business name or limited company registration, KRA PIN and tax obligation registration (including VAT and eTIMS onboarding where required), county licences, and NSSF/SHIF employer registration. For foreign investors, we guide you through the additional requirements for directorship and PIN registration. Beyond registration, we advise on the right structure for your goals so you start on a foundation built for growth — not future tax headaches.",
    features: [
      "Business name and limited company registration",
      "KRA PIN registration (individuals and entities)",
      "Tax obligation registration (VAT, PAYE, turnover tax)",
      "eTIMS onboarding where required",
      "County business licence applications",
      "NSSF and SHIF employer registration",
      "Foreign investor directorship and PIN guidance",
      "Business structure advisory",
    ],
    process: [
      {
        step: 1,
        title: "Structure Consultation",
        description:
          "Advise on the right business structure for your goals and tax position.",
      },
      {
        step: 2,
        title: "Document Preparation",
        description:
          "Prepare and review all required registration documents.",
      },
      {
        step: 3,
        title: "Registration & Filing",
        description:
          "Submit applications for company, KRA, statutory, and county registrations.",
      },
      {
        step: 4,
        title: "Compliance Setup",
        description:
          "Confirm all registrations, set up obligations, and brief you on ongoing requirements.",
      },
    ],
    faqs: [
      {
        question: "What registrations does a new business need in Kenya?",
        answer:
          "At minimum: business name or company registration, KRA PIN, and employer registrations for NSSF and SHIF. VAT, eTIMS, and county licences apply depending on your business type and turnover.",
      },
      {
        question: "Can foreign nationals register a business in Kenya?",
        answer:
          "Yes. Foreign investors and directors can register businesses in Kenya, though there are additional requirements for PIN registration and directorship that we guide clients through.",
      },
    ],
    relatedServices: [
      "tax-advisory-planning",
      "bookkeeping-accounting",
      "payroll-management",
    ],
    metaTitle: "Business Registration & Start-Up Support Kenya | Rosewood Consulting",
    metaDescription:
      "End-to-end business registration and tax setup for new businesses in Kenya. KRA PIN, VAT, eTIMS, NSSF, and county licences handled by Rosewood Consulting.",
  },
  {
    id: "8",
    slug: "advisory-growth-strategy",
    title: "Advisory for Growth & Strategy",
    shortDescription:
      "Beyond compliance, we help you think bigger — financial planning, cash flow management, business structuring, and decision support that turns your numbers into strategy.",
    image: "/images/services/advisory-growth.webp",
    fullDescription:
      "Compliance keeps you safe; strategy moves you forward. Our advisory service supports business owners with budgeting and cash flow planning, financial analysis, pricing and profitability reviews, and guidance on restructuring, expansion, or investment decisions. We translate your financial data into plain-language insight, so every major decision is informed, deliberate, and aligned with where you want your business to go.",
    features: [
      "Budgeting and cash flow planning",
      "Financial analysis and reporting",
      "Pricing and profitability reviews",
      "Business restructuring guidance",
      "Expansion and investment decision support",
      "Plain-language financial insight",
      "Risk identification and mitigation",
      "Long-term growth planning",
    ],
    process: [
      {
        step: 1,
        title: "Financial Review",
        description:
          "Analyse your current financial position, trends, and business goals.",
      },
      {
        step: 2,
        title: "Insight & Analysis",
        description:
          "Translate your numbers into clear, actionable insight.",
      },
      {
        step: 3,
        title: "Strategy Development",
        description:
          "Develop practical recommendations for growth, efficiency, or restructuring.",
      },
      {
        step: 4,
        title: "Ongoing Partnership",
        description:
          "Remain your advisory partner through execution and beyond.",
      },
    ],
    faqs: [
      {
        question: "How is advisory different from tax compliance?",
        answer:
          "Compliance keeps you on the right side of KRA. Advisory goes further — helping you use your financial data to make better business decisions, plan for growth, and manage risk strategically.",
      },
      {
        question: "What kind of businesses benefit from growth advisory?",
        answer:
          "Any business owner who wants to move beyond simply filing returns and start using their financial information to drive deliberate, informed decisions.",
      },
    ],
    relatedServices: [
      "tax-advisory-planning",
      "bookkeeping-accounting",
      "corporate-training",
    ],
    metaTitle: "Business Advisory & Growth Strategy Kenya | Rosewood Consulting",
    metaDescription:
      "Financial planning, cash flow management, and business growth advisory for Kenyan businesses. Turn your numbers into strategy with Rosewood Consulting.",
  },
  {
    id: "9",
    slug: "corporate-training",
    title: "Corporate Training & Workshops",
    shortDescription:
      "Practical, hands-on training for entrepreneurs and teams — covering taxation, bookkeeping, payroll, and financial management — so your people can handle compliance with confidence.",
    image: "/images/services/corporate-training.webp",
    fullDescription:
      "We deliver tailored training sessions and workshops for SMEs, corporate teams, SACCOs, and professional groups. Topics include Kenyan tax compliance essentials, eTIMS and VAT, payroll administration, bookkeeping fundamentals, and financial decision-making for business owners. Sessions are practical, scenario-based, and customised to your industry — available in person or virtually.",
    features: [
      "Kenyan tax compliance essentials",
      "eTIMS and VAT compliance training",
      "Payroll administration and PAYE",
      "Bookkeeping fundamentals",
      "Financial decision-making for business owners",
      "Customised in-house programmes",
      "SME, corporate, and SACCO workshops",
      "In-person and virtual delivery",
    ],
    process: [
      {
        step: 1,
        title: "Needs Assessment",
        description:
          "Identify your team's knowledge gaps and training objectives.",
      },
      {
        step: 2,
        title: "Programme Design",
        description:
          "Customise training content and materials to your industry and team level.",
      },
      {
        step: 3,
        title: "Training Delivery",
        description:
          "Deliver engaging, scenario-based sessions in person or virtually.",
      },
      {
        step: 4,
        title: "Assessment & Follow-up",
        description:
          "Assess learning outcomes and provide post-training support.",
      },
    ],
    faqs: [
      {
        question: "Who are these training sessions designed for?",
        answer:
          "Our workshops are tailored for SME owners, corporate finance teams, SACCOs, and professional groups who want practical, applicable knowledge of Kenyan tax and financial compliance.",
      },
      {
        question: "Can training be done online?",
        answer:
          "Yes. We offer both in-person and virtual training sessions to accommodate teams across different locations.",
      },
    ],
    relatedServices: [
      "tax-advisory-planning",
      "bookkeeping-accounting",
      "payroll-management",
    ],
    metaTitle: "Corporate Tax Training & Workshops Kenya | Rosewood Consulting",
    metaDescription:
      "Practical tax, bookkeeping, and payroll training for businesses and teams in Kenya. Customised workshops by Rosewood Consulting — in person or virtual.",
  },
];

export const blogPosts: BlogPost[] = [
  {
    id: "1",
    slug: "etims-mandatory-compliance-guide-2024",
    title:
      "The Complete Guide to eTIMS Compliance for Kenyan Businesses in 2024",
    excerpt:
      "Everything VAT-registered businesses need to know about KRA's Electronic Tax Invoice Management System — deadlines, requirements, and how to get compliant fast.",
    content: `The Kenya Revenue Authority's Electronic Tax Invoice Management System (eTIMS) has fundamentally changed how businesses in Kenya manage their invoicing and VAT compliance. Since becoming mandatory for all VAT-registered taxpayers, eTIMS has become one of the most talked-about compliance topics in Kenyan business circles.\n\n## What is eTIMS?\n\neTIMS is a digital system that allows businesses to generate, transmit, and store tax invoices electronically through KRA-approved devices or integrated software solutions. The system creates a real-time audit trail of all taxable transactions, making VAT fraud significantly harder to perpetrate.\n\n## Who Needs to Comply?\n\nAll VAT-registered businesses in Kenya are required to use eTIMS. This includes businesses that are voluntarily registered for VAT and those that exceed the mandatory registration threshold of KES 5 million in annual turnover.\n\n## Penalties for Non-Compliance\n\nFailure to comply with eTIMS requirements attracts significant penalties, including denial of input VAT claims on non-eTIMS invoices and potential prosecution for tax evasion.`,
    author: {
      id: "1",
      name: "David Mwangi",
      role: "Senior Tax Consultant",
      avatar: "",
      bio: "David has over 12 years of experience in Kenyan tax law and compliance.",
    },
    category: "eTIMS & VAT",
    tags: ["eTIMS", "VAT", "KRA", "Compliance", "Tax Invoicing"],
    publishedAt: "2024-03-15",
    readTime: 8,
    coverImage: "/images/blog/etims-mandatory-compliance-guide-2024.png",
    featured: true,
    metaTitle: "eTIMS Compliance Guide 2024 Kenya | Rosewood Consulting",
    metaDescription:
      "Complete guide to eTIMS compliance for Kenyan businesses. Understand requirements, deadlines, and steps to get compliant with KRA's invoicing system.",
  },
  {
    id: "2",
    slug: "kra-audit-what-to-expect-and-how-to-prepare",
    title: "KRA Audit: What to Expect and How to Prepare Your Business",
    excerpt:
      "A KRA audit doesn't have to end in a crippling assessment. Learn what triggers audits, what the process looks like, and how to protect your business from unfair assessments.",
    content: `Receiving a KRA audit notice can trigger immediate anxiety among business owners. However, with proper preparation and the right support, an audit can be navigated successfully. Understanding the process is the first step to protecting your business interests.`,
    author: {
      id: "2",
      name: "Sarah Njoroge",
      role: "Tax Dispute Specialist",
      avatar: "",
      bio: "Sarah specialises in KRA audit defence and has successfully resolved hundreds of tax disputes.",
    },
    category: "KRA Audit",
    tags: ["KRA Audit", "Tax Dispute", "Compliance", "Business"],
    publishedAt: "2024-02-28",
    readTime: 10,
    coverImage: "/images/blog/kra-audit-what-to-expect-and-how-to-prepare.jpeg",
    featured: true,
    metaTitle: "KRA Audit Preparation Guide | Rosewood Consulting Kenya",
    metaDescription:
      "Prepare your business for a KRA audit. Learn what triggers audits, the audit process, and how to protect your business interests.",
  },
  {
    id: "3",
    slug: "transfer-pricing-kenya-compliance-guide",
    title: "Transfer Pricing Compliance in Kenya: A Practical Business Guide",
    excerpt:
      "Understanding transfer pricing obligations for multinational companies operating in Kenya — documentation requirements, risk areas, and best practices.",
    content: `Transfer pricing has become one of the most scrutinised areas of taxation globally, and Kenya is no exception. The Kenya Revenue Authority has significantly increased its focus on transfer pricing compliance among multinational enterprises operating in the country.`,
    author: {
      id: "3",
      name: "James Kariuki",
      role: "International Tax Advisor",
      avatar: "",
      bio: "James specialises in international tax and transfer pricing for multinational corporations.",
    },
    category: "International Tax",
    tags: [
      "Transfer Pricing",
      "International Tax",
      "Multinationals",
      "Compliance",
    ],
    publishedAt: "2024-02-10",
    readTime: 12,
    coverImage: "/images/blog/transfer-pricing-kenya-compliance-guide.jpg",
    featured: false,
    metaTitle: "Transfer Pricing Kenya Guide | Rosewood Consulting",
    metaDescription:
      "Comprehensive guide to transfer pricing compliance in Kenya. Documentation requirements, KRA expectations, and risk management.",
  },
  {
    id: "4",
    slug: "paye-changes-kenya-finance-act-2024",
    title:
      "PAYE Changes Under Kenya's Finance Act 2024: What Employers Must Know",
    excerpt:
      "The Finance Act 2024 introduced significant changes to PAYE rates and allowances. Here's what every employer in Kenya needs to understand and action immediately.",
    content: `Kenya's Finance Act 2024 brought several important amendments to the Pay As You Earn (PAYE) tax framework. Employers must understand and implement these changes to remain compliant and avoid penalties.`,
    author: {
      id: "1",
      name: "David Mwangi",
      role: "Senior Tax Consultant",
      avatar: "",
      bio: "David has over 12 years of experience in Kenyan tax law and compliance.",
    },
    category: "PAYE & Payroll",
    tags: ["PAYE", "Finance Act", "Payroll", "Employment Tax"],
    publishedAt: "2024-01-20",
    readTime: 7,
    coverImage: "/images/blog/paye-changes-kenya-finance-act-2024.jpeg",
    featured: false,
    metaTitle: "PAYE Finance Act 2024 Kenya | Rosewood Consulting",
    metaDescription:
      "Essential guide to PAYE changes under Kenya's Finance Act 2024. New rates, bands, and employer compliance requirements explained.",
  },
  {
    id: "5",
    slug: "vat-exemptions-kenya-complete-list",
    title:
      "VAT Exemptions and Zero-Rated Supplies in Kenya: The Complete Business Guide",
    excerpt:
      "Not sure if your products or services attract VAT? This comprehensive guide covers all VAT exemptions and zero-rated supplies under Kenyan tax law.",
    content: `Understanding which goods and services are exempt from VAT and which are zero-rated is essential for accurate tax compliance in Kenya. Many businesses inadvertently over-charge or under-charge VAT due to confusion in this area.`,
    author: {
      id: "2",
      name: "Sarah Njoroge",
      role: "Tax Dispute Specialist",
      avatar: "",
      bio: "Sarah specialises in KRA audit defence and has successfully resolved hundreds of tax disputes.",
    },
    category: "eTIMS & VAT",
    tags: ["VAT", "Tax Exemptions", "Zero-Rated", "Kenya Tax Law"],
    publishedAt: "2024-01-08",
    readTime: 9,
    coverImage: "/images/blog/vat-exemptions-kenya-complete-list.jpeg",
    featured: true,
    metaTitle: "VAT Exemptions Kenya Guide | Rosewood Consulting",
    metaDescription:
      "Complete guide to VAT exemptions and zero-rated supplies in Kenya. Know your obligations and avoid costly compliance mistakes.",
  },
  {
    id: "6",
    slug: "starting-business-kenya-tax-checklist",
    title:
      "Starting a Business in Kenya: Your Complete Tax Compliance Checklist",
    excerpt:
      "Everything a new business owner needs to set up tax compliance correctly from the start — from KRA PIN to NSSF, SHIF, and eTIMS.",
    content: `Starting a business in Kenya is exciting, but getting your tax compliance right from the beginning is essential. Mistakes early on can lead to penalties, missed deductions, and headaches later.`,
    author: {
      id: "3",
      name: "James Kariuki",
      role: "International Tax Advisor",
      avatar: "",
      bio: "James specialises in international tax and transfer pricing for multinational corporations.",
    },
    category: "Business Setup",
    tags: ["New Business", "KRA Registration", "Tax Compliance", "Startup"],
    publishedAt: "2023-12-15",
    readTime: 6,
    coverImage: "/images/blog/starting-business-kenya-tax-checklist.png",
    featured: false,
    metaTitle: "New Business Tax Checklist Kenya | Rosewood Consulting",
    metaDescription:
      "Complete tax compliance checklist for new businesses in Kenya. KRA PIN, VAT, PAYE, NSSF, SHIF, and eTIMS setup guide.",
  },
];

export const testimonials: Testimonial[] = [
  {
    id: "1",
    name: "Catherine Wanjiku",
    company: "Savannah Foods Ltd",
    role: "CEO",
    content:
      "Rosewood Consulting transformed our tax compliance. They handled our eTIMS setup flawlessly and saved us from a potentially disastrous KRA audit. Their expertise is genuinely world-class.",
    rating: 5,
    avatar: "",
    service: "Tax Compliance Health Check",
    featured: true,
  },
  {
    id: "2",
    name: "Michael Ochieng",
    company: "TechBridge Africa",
    role: "CFO",
    content:
      "After a nerve-wracking KRA audit notice, Rosewood stepped in and managed everything professionally. Not only did they successfully defend us, but they also restructured our tax position to prevent future exposure.",
    rating: 5,
    avatar: "",
    service: "KRA Audit Support & Dispute Resolution",
    featured: true,
  },
  {
    id: "3",
    name: "Priya Patel",
    company: "Meridian Holdings",
    role: "Managing Director",
    content:
      "As a holding company with subsidiaries across East Africa, our tax situation is complex. Rosewood's advisory team provides clear, practical guidance that has saved us millions in unnecessary tax exposure.",
    rating: 5,
    avatar: "",
    service: "Tax Advisory & Planning",
    featured: true,
  },
  {
    id: "4",
    name: "James Mutua",
    company: "Equator Logistics",
    role: "Finance Manager",
    content:
      "The bookkeeping and payroll service from Rosewood is impeccable. Our accounts are always clean, payroll is never late, and every statutory filing is done on time. Peace of mind is priceless.",
    rating: 5,
    avatar: "",
    service: "Bookkeeping & Accounting Services",
    featured: false,
  },
  {
    id: "5",
    name: "Amina Hassan",
    company: "Coastal Properties",
    role: "Director",
    content:
      "The tax training workshop Rosewood conducted for our finance team was outstanding. Practical, engaging, and immediately applicable. Our team's confidence in handling compliance has increased dramatically.",
    rating: 5,
    avatar: "",
    service: "Corporate Training & Workshops",
    featured: false,
  },
  {
    id: "6",
    name: "Robert Kimani",
    company: "Digital Frontier Ltd",
    role: "Founder",
    content:
      "From company registration to monthly returns filing, Rosewood handles everything seamlessly. Starting a business was stressful enough — having them as our tax partner from day one made all the difference.",
    rating: 5,
    avatar: "",
    service: "Business Registration & Start-Up Support",
    featured: false,
  },
];

export const teamMembers: TeamMember[] = [
  {
    id: "1",
    name: "Diana Rose",
    role: "Managing Partner",
    bio: "A Certified Public Accountant with over 20 years of experience in Kenyan taxation, Elizabeth founded Rosewood Consulting with a vision to give every business access to clear, honest, expert financial guidance. She has represented clients before the Tax Appeals Tribunal and advised on landmark tax cases across East Africa.",
    qualifications: ["CPA (K)", "FCCA", "LLB (Tax Law)", "MBA (Finance)"],
    avatar: "",
    email: "info@rosewoodconsulting.co.ke",
    linkedin: "https://linkedin.com",
  },
  {
    id: "2",
    name: "David Mwangi",
    role: "Senior Tax Consultant",
    bio: "David specialises in corporate taxation and KRA compliance. With over 12 years of experience advising businesses across Kenya, he brings deep expertise in tax returns, advisory, and audit defence — translating complex tax law into clear, actionable strategies for his clients.",
    qualifications: ["CPA (K)", "Masters in Taxation", "ICPAK Member"],
    avatar: "",
    email: "info@rosewoodconsulting.co.ke",
  },
  {
    id: "3",
    name: "Sarah Njoroge",
    role: "Tax Dispute & Audit Specialist",
    bio: "Sarah has successfully defended hundreds of KRA audit cases and is one of Kenya's foremost experts in tax dispute resolution. Her in-depth knowledge of KRA processes — objections, ADR, and the Tax Appeals Tribunal — gives clients a decisive advantage in contentious matters.",
    qualifications: ["CPA (K)", "LLB", "ADR Certified", "ICPAK Member"],
    avatar: "",
    email: "info@rosewoodconsulting.co.ke",
  },
  {
    id: "4",
    name: "James Kariuki",
    role: "International Tax Advisor",
    bio: "James leads our international and cross-border tax practice. He brings extensive experience in transfer pricing documentation, double tax treaty analysis, and advising multinational enterprises on their East African tax obligations.",
    qualifications: ["CPA (K)", "ADIT (International Tax)", "LLM Tax"],
    avatar: "",
    email: "info@rosewoodconsulting.co.ke",
  },
];

export const resources: Resource[] = [
  {
    id: "1",
    title: "KRA Tax Filing Calendar 2024",
    description:
      "Comprehensive calendar of all KRA filing and payment deadlines for 2024, covering income tax, VAT, PAYE, and withholding tax.",
    type: "pdf",
    category: "Compliance",
    downloadUrl: "#",
    fileSize: "245 KB",
    uploadedAt: "2024-01-02",
    downloads: 1240,
    featured: true,
  },
  {
    id: "2",
    title: "eTIMS Implementation Checklist",
    description:
      "Step-by-step checklist for VAT-registered businesses transitioning to KRA's Electronic Tax Invoice Management System.",
    type: "checklist",
    category: "eTIMS",
    downloadUrl: "#",
    fileSize: "180 KB",
    uploadedAt: "2024-02-10",
    downloads: 890,
    featured: true,
  },
  {
    id: "3",
    title: "Business Tax Guide Kenya 2024",
    description:
      "Comprehensive guide to corporate and business taxes in Kenya for 2024, including Finance Act updates and compliance obligations.",
    type: "guide",
    category: "Corporate Tax",
    downloadUrl: "#",
    fileSize: "1.2 MB",
    uploadedAt: "2024-01-15",
    downloads: 2100,
    featured: true,
  },
  {
    id: "4",
    title: "PAYE Computation Template",
    description:
      "Excel template for accurate PAYE computation including NSSF, SHIF, and Housing Levy deductions.",
    type: "template",
    category: "PAYE & Payroll",
    downloadUrl: "#",
    fileSize: "95 KB",
    uploadedAt: "2024-01-20",
    downloads: 560,
    featured: false,
  },
  {
    id: "5",
    title: "Transfer Pricing Documentation Guide",
    description:
      "Guide to preparing transfer pricing documentation for KRA compliance for multinationals operating in Kenya.",
    type: "guide",
    category: "International Tax",
    downloadUrl: "#",
    fileSize: "680 KB",
    uploadedAt: "2024-02-05",
    downloads: 320,
    featured: false,
  },
  {
    id: "6",
    title: "Finance Act 2024 Tax Changes Summary",
    description:
      "Plain-language summary of key tax changes introduced by Kenya's Finance Act 2024 and their impact on businesses.",
    type: "pdf",
    category: "Updates",
    downloadUrl: "#",
    fileSize: "320 KB",
    uploadedAt: "2024-07-01",
    downloads: 1890,
    featured: false,
  },
];

export const mediaItems: MediaItem[] = [
  {
    id: "1",
    title: "Tax Compliance Summit 2025",
    description: "Rosewood Consulting at the annual East Africa Tax Compliance Summit.",
    type: "image",
    url: "/images/media/tax-compliance-summit-2025-thumb.png",
    thumbnail: "/images/media/tax-compliance-summit-2025-thumb.jpg",
    category: "Events",
    uploadedAt: "2025-03-20",
    featured: true,
  },
  {
    id: "2",
    title: "eTIMS Workshop Highlights",
    description: "Highlights from our eTIMS compliance workshop for Nairobi SMEs.",
    type: "video",
    url: "/images/media/etims-workshop-highlights-thumb.png",
    thumbnail: "/images/media/etims-workshop-highlights-thumb.png",
    category: "Training",
    uploadedAt: "2024-02-15",
    featured: true,
  },
  {
    id: "3",
    title: "End-of-Year Tax Seminar",
    description: "Annual tax planning seminar for Nairobi business leaders and finance teams.",
    type: "image",
    url: "/images/media/end-of-year-tax-seminar-thumb.png",
    thumbnail: "/images/media/end-of-year-tax-seminar-thumb.png",
    category: "Events",
    uploadedAt: "2023-11-30",
    featured: false,
  },
    {
    id: "4",
    title: "Rosewood Consulting Corporate Profile",
    description:
      "A glimpse into Rosewood Consulting's commitment to delivering trusted tax advisory, compliance, bookkeeping, and business consulting services across Kenya.",
    type: "poster",
    url: "/images/media/rosewood-corporate-profile-thumb.png",
    thumbnail: "/images/media/rosewood-corporate-profile-thumb.png",
    category: "Posters",
    uploadedAt: "2025-02-28",
    featured: true,
  },
];

export const serviceCategories = [
  "All Services",
  "Registration & Setup",
  "Compliance",
  "Advisory",
  "Training",
];

export const blogCategories = [
  "All Posts",
  "eTIMS & VAT",
  "KRA Audit",
  "International Tax",
  "PAYE & Payroll",
  "Business Setup",
  "Updates",
];